import { Box } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { BorrowingBaseSummaryConcentrationLimitAccordion } from './BorrowingBaseSummaryConcentrationLimits';
import { BorrowingBaseSummaryEligibility } from './BorrowingBaseSummaryEligibility';
import { BorrowingBaseSummaryReceivablesLoanProductCalculations } from './BorrowingBaseSummaryReceivablesLoanProductCalculations';
import {
  BorrowingBaseSummarySectionHeader,
  BorrowingBaseSummarySubSectionHeader,
} from './BorrowingBaseSummarySectionHeader';
import {
  BorrowingBaseSummaryInput,
  getColumnValuesForBorrowingBaseReportField,
  groupMultiLpConcentrationKeysByLoanProducts,
  LinkAddressAndLabel,
} from './utils';

type BorrowingBaseSummaryReceivablesProps = {
  borrowingBaseReports: BorrowingBaseSummaryInput;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  showComparisonAndDifferenceColumns: boolean;
  eligibilityLink?: LinkAddressAndLabel;
  concentrationLink?: LinkAddressAndLabel;
};
export const BorrowingBaseSummaryReceivables = ({
  borrowingBaseReports,
  creditAgreement,
  showComparisonAndDifferenceColumns,
  eligibilityLink,
  concentrationLink,
}: BorrowingBaseSummaryReceivablesProps) => {
  const { creditFacilityId } = creditAgreement;

  const loanProductCalculationsForBorrowingBaseReports = {
    minuendBorrowingBaseReport:
      borrowingBaseReports.minuendBorrowingBaseReport.loanProductCalculations,
    subtrahendBorrowingBaseReport:
      borrowingBaseReports.subtrahendBorrowingBaseReport
        ?.loanProductCalculations,
    referenceBorrowingBaseReport:
      borrowingBaseReports.referenceBorrowingBaseReport
        ?.loanProductCalculations,
  };

  const {
    borrowingBaseMechanics: {
      calcAgentMechanics: {
        loanProductMechanics,
        multiLoanProductConcentrationLimits,
      },
    },
  } = creditAgreement;

  const multiLpConcentrationKeysByLoanProductsGrouping = groupMultiLpConcentrationKeysByLoanProducts(
    { multiLoanProductConcentrationLimits },
  );

  // borrowing base reports before 2023-12-19 will not have loan product calculations associated
  // therefore we will only display the per-lp dropdowns in the borrowing base receivables summary if they exist
  const displayLoanProductCalculations =
    !!borrowingBaseReports.minuendBorrowingBaseReport.loanProductCalculations &&
    !!borrowingBaseReports.minuendBorrowingBaseReport.loanProductCalculations
      .length;

  const isSingleLoanProduct = loanProductMechanics.length === 1;

  return (
    <Box w="100%">
      <BorrowingBaseSummarySectionHeader headerLabel="Receivables" />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Receivable Exposure"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalReceivableExposure',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalReceivableExposure"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="totalReceivableExposure"
              valueType="currency"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />

      <BorrowingBaseSummarySubSectionHeader
        headerLabel="Eligibility"
        linkAddress={eligibilityLink?.linkAddress}
        linkLabel={eligibilityLink?.linkLabel}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Eligibility Kickout"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalEligibilityKickout',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalEligibilityKickout"
          />
        )}
        accordionPanel={
          <Box>
            {_.map(loanProductMechanics, (loanProduct) => {
              const eligibilityCriteriaKeysForLoanProduct = _.compact(
                _.map(loanProduct.eligibilityTests, (eligibilityTest) => {
                  const propertyKey =
                    eligibilityTest._config_?.eligibilityTestConfig
                      ?.propertyKey;
                  return propertyKey;
                }),
              );

              const eligibilityCriteriaCalculationsMinuend = _.filter(
                borrowingBaseReports.minuendBorrowingBaseReport
                  .eligibilityCriteriaCalculations,
                (calc) =>
                  _.includes(
                    eligibilityCriteriaKeysForLoanProduct,
                    calc.eligibilityCriteriaKey,
                  ),
              );

              const eligibilityCriteriaCalculationsSubtrahend = _.filter(
                borrowingBaseReports.subtrahendBorrowingBaseReport
                  ?.eligibilityCriteriaCalculations,
                (calc) =>
                  _.includes(
                    eligibilityCriteriaKeysForLoanProduct,
                    calc.eligibilityCriteriaKey,
                  ),
              );

              const eligibilityCriteriaCalculationsReference3 = _.filter(
                borrowingBaseReports.referenceBorrowingBaseReport
                  ?.eligibilityCriteriaCalculations,
                (calc) =>
                  _.includes(
                    eligibilityCriteriaKeysForLoanProduct,
                    calc.eligibilityCriteriaKey,
                  ),
              );

              return (
                <BorrowingBaseSummaryEligibility
                  key={`eligibility-for-loan-product-${loanProduct.entity.slug}`}
                  loanProductSlug={loanProduct.entity.name}
                  loanProductId={loanProduct.entity.id}
                  loanProductCalculations={
                    loanProductCalculationsForBorrowingBaseReports
                  }
                  eligibilityCriteriaCalculationsForLoanProduct={{
                    minuendBorrowingBaseReport: eligibilityCriteriaCalculationsMinuend,
                    subtrahendBorrowingBaseReport: eligibilityCriteriaCalculationsSubtrahend,
                    referenceBorrowingBaseReport: eligibilityCriteriaCalculationsReference3,
                  }}
                  showComparisonAndDifferenceColumns={
                    showComparisonAndDifferenceColumns
                  }
                  defaultIsExpanded={isSingleLoanProduct}
                />
              );
            })}
          </Box>
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Eligible Exposure"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalEligibleExposure',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalEligibleExposure"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="totalEligibleExposure"
              valueType="currency"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />

      <BorrowingBaseSummarySubSectionHeader
        headerLabel="Concentration Limits"
        linkAddress={concentrationLink?.linkAddress}
        linkLabel={concentrationLink?.linkLabel}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Concentration Kickout"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalConcentrationKickout',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="totalConcentrationKickout"
          />
        )}
        accordionPanel={
          <Box>
            {_.map(loanProductMechanics, (loanProduct) => {
              const concentrationLimitKeysForLoanProduct = _.compact(
                _.map(loanProduct.concentrationLimits, (concentrationLimit) => {
                  const propertyKey =
                    concentrationLimit._config_?.concentrationLimitConfig
                      ?.propertyKey;
                  return propertyKey;
                }),
              );

              const concentrationLimitCalculationsMinuend = _.filter(
                borrowingBaseReports.minuendBorrowingBaseReport
                  .concentrationLimitCalculations,
                (calc) =>
                  _.includes(
                    concentrationLimitKeysForLoanProduct,
                    calc.concentrationLimitKey,
                  ),
              );

              const concentrationLimitCalculationsSubtrahend = _.filter(
                borrowingBaseReports.subtrahendBorrowingBaseReport
                  ?.concentrationLimitCalculations,
                (calc) =>
                  _.includes(
                    concentrationLimitKeysForLoanProduct,
                    calc.concentrationLimitKey,
                  ),
              );

              const concentrationLimitCalculationsReference = _.filter(
                borrowingBaseReports.referenceBorrowingBaseReport
                  ?.concentrationLimitCalculations,
                (calc) =>
                  _.includes(
                    concentrationLimitKeysForLoanProduct,
                    calc.concentrationLimitKey,
                  ),
              );

              return (
                <BorrowingBaseSummaryConcentrationLimitAccordion
                  key={`concentration-limits-for-loan-product-${loanProduct.entity.slug}`}
                  borrowingBaseReports={borrowingBaseReports}
                  loanProductCalculations={
                    loanProductCalculationsForBorrowingBaseReports
                  }
                  concentrationLimitCalculationsForLoanProduct={{
                    minuendBorrowingBaseReport: concentrationLimitCalculationsMinuend,
                    subtrahendBorrowingBaseReport: concentrationLimitCalculationsSubtrahend,
                    referenceBorrowingBaseReport: concentrationLimitCalculationsReference,
                  }}
                  creditAgreement={creditAgreement}
                  loanProductId={loanProduct.entity.id}
                  loanProductLabel={commonUtils.toTitleCase(
                    loanProduct.entity.name,
                  )}
                  showComparisonAndDifferenceColumns={
                    showComparisonAndDifferenceColumns
                  }
                  defaultIsExpanded={isSingleLoanProduct}
                />
              );
            })}
            {_.map(
              multiLpConcentrationKeysByLoanProductsGrouping,
              (multiLpConcentrationKeys, lpGroupingSlugsString) => {
                const calcsForGroupingMinuend = _.filter(
                  borrowingBaseReports.minuendBorrowingBaseReport
                    .concentrationLimitCalculations,
                  (calc) =>
                    _.includes(
                      multiLpConcentrationKeys,
                      calc.concentrationLimitKey,
                    ),
                );

                const calcsForGroupingSubtrahend = _.filter(
                  borrowingBaseReports.subtrahendBorrowingBaseReport
                    ?.concentrationLimitCalculations,
                  (calc) =>
                    _.includes(
                      multiLpConcentrationKeys,
                      calc.concentrationLimitKey,
                    ),
                );

                const calcsForGroupingReference = _.filter(
                  borrowingBaseReports.referenceBorrowingBaseReport
                    ?.concentrationLimitCalculations,
                  (calc) =>
                    _.includes(
                      multiLpConcentrationKeys,
                      calc.concentrationLimitKey,
                    ),
                );

                return (
                  <BorrowingBaseSummaryConcentrationLimitAccordion
                    key={`multi-lp-concentration-limit-${lpGroupingSlugsString}`}
                    borrowingBaseReports={borrowingBaseReports}
                    loanProductCalculations={
                      loanProductCalculationsForBorrowingBaseReports
                    }
                    concentrationLimitCalculationsForLoanProduct={{
                      minuendBorrowingBaseReport: calcsForGroupingMinuend,
                      subtrahendBorrowingBaseReport: calcsForGroupingSubtrahend,
                      referenceBorrowingBaseReport: calcsForGroupingReference,
                    }}
                    creditAgreement={creditAgreement}
                    // @dani add lpId once we are ready to manage mlp concentration limits per lp from the new concentration_limit_multi_loan_product_calculations table
                    loanProductLabel={lpGroupingSlugsString}
                    showComparisonAndDifferenceColumns={
                      showComparisonAndDifferenceColumns
                    }
                    defaultIsExpanded={isSingleLoanProduct}
                  />
                );
              },
            )}
          </Box>
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Eligible Exposure Less Concentration Kickouts"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'borrowingBaseAmountAssets', // will be renamed in migration phase 3 to eligibleExposureLessConcentrationKickouts
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="borrowingBaseAmountAssets"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="totalEligibleExposureLessConcentrationKickouts"
              valueType="currency"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />

      <BorrowingBaseSummarySubSectionHeader headerLabel="Receivables Borrowing Base" />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Weighted Average Receivable Advance Rate"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'weightedAverageReceivableAdvanceRate',
            })}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="weightedAverageReceivableAdvanceRate"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="weightedAverageReceivableAdvanceRate"
              valueType="percentage"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Receivable Borrowing Base Amount"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'maxReceivableAdvanceAmount', // will be renamed in migration phase 3 to totalReceivableBorrowingBaseAmount
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="maxReceivableAdvanceAmount"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="totalReceivableBorrowingBaseAmount"
              valueType="currency"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Effective Receivable Advance Rate"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'effectiveReceivableAdvanceRate',
            })}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            fieldName="effectiveReceivableAdvanceRate"
          />
        )}
        accordionPanel={
          displayLoanProductCalculations ? (
            <BorrowingBaseSummaryReceivablesLoanProductCalculations
              creditFacilityId={creditFacilityId}
              loanProductCalculations={
                loanProductCalculationsForBorrowingBaseReports
              }
              borrowingBaseReportField="effectiveReceivableAdvanceRate"
              valueType="percentage"
              showComparisonAndDifferenceColumns={
                showComparisonAndDifferenceColumns
              }
            />
          ) : undefined
        }
      />
    </Box>
  );
};
